import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styles from "./AssessmentInviteForm.module.scss";
import { Field, Formik, FormikProps } from 'formik';
import { Input } from '../common/Input';
import * as yup from 'yup';
import { ButtonPageLayout } from '../common/ButtonPageLayout';
import { UserContext } from '../../context/UserContext';
import { AssessmentInviteData } from '../../apollo/types/AssessmentInviteData';
import { AssessmentRoute } from '../../apollo/types/AssessmentRoute';
import { TagFormInput } from '../common/TagFormInput';
import { Tag } from 'react-tag-autocomplete';
import { useQuery } from '@apollo/client';
import { TAG_SUGGESTIONS } from '../../apollo/queries';

type AssessmentInviteProps = {
  isOpen: boolean;
  onToggle: Function;
  onClick: Function;
  sendingSuccessful: Boolean
  routes: [AssessmentRoute] | []
  errorFound: Boolean
  preSelectedAssessmentRoute?: null | number
};

const validationSchema = yup.object().shape({
    name: yup.string().required(),
    emailAddress: yup.string().email().required(),
    venture: yup.string().required(),
    language: yup.string().oneOf(['nl', 'en']),
});

const letterNL = `
Beste [ondernemer],

Wij zijn benieuwd welke groeistappen [venture] maakt. Door onze vragenlijst in te vullen kunnen we je inzicht geven in de groeistappen en hoe jij meer focus kunt aanbrengen om jouw groei te versnellen.

* Naar de vragenlijst van de [assessment_name]©

Na het invullen van de vragenlijst krijg je een rapport met inzichten over [venture] voor de volgende groeistap en kun jij de nodige activiteiten beter prioriteren.

Mocht je vragen hebben over de vragenlijst of het rapport, schroom dan niet om contact met mij op te nemen op [email begeleider].

Hartelijke groet,

[begeleider]
`;

const letterEN = `Dear [ondernemer],

We are curious about what growth steps [venture] is making. By completing our questionnaire, we can give you insight into the growth steps and how you can create more focus to accelerate your growth.

* To the questionnaire of the [assessment_name]©

After completing the questionnaire, you will receive a report with insights about [venture] for the next growth step and you can better prioritize the necessary activities.

If you have any questions about the questionnaire or the report, please do not hesitate to contact me at [email begeleider].

Sincerely,
[begeleider]
`;

export const AssessmentInviteForm = ({
  isOpen,
  onToggle,
  onClick,
  sendingSuccessful,
  routes,
  errorFound,
  preSelectedAssessmentRoute = null
}: AssessmentInviteProps) => {
    const initialFormValues = {
        name: "",
        emailAddress: "",
        venture: "",
        language: "nl",
        assessmentRoute: preSelectedAssessmentRoute,
        tags: [] = []
    };


  const [tags, setTags] = useState<[Tag] | []>([])
  const formikRef = useRef<FormikProps<AssessmentInviteProps>>(null);
  const [message, setMessage] = useState<String | null>(null);
  const [nameValidation, setNameValidation] = useState(false);
  const [formData, setFormData] = useState<AssessmentInviteData>(initialFormValues)
  const [formValid, setFormValid] = useState(false)
  const userContext = useContext(UserContext)
    const [tagSuggestions, setTagSuggestions] = useState([]);

    useQuery(TAG_SUGGESTIONS, {
        onCompleted: (someData) => {
            setTagSuggestions(someData.tagSuggestions)
        },
        fetchPolicy: "no-cache"
    });


    const submitForm = ( values: {
      name: string,
      emailAddress: string,
      venture: string,
      language: string
      assessmentRoute: number | null
      },
    ) => {
      const { name, emailAddress, venture, language, assessmentRoute } = values;

      let letter = ''
      if(language === 'nl') {
          letter = letterNL
      } else {
          letter = letterEN
      }

      let chosenAssessmentRoute = null
      let chosenAssessmentName = 'ConsistencyScan'
      if(routes && routes.length) {
          if(assessmentRoute === null) {
              chosenAssessmentRoute = routes[0].id
          } else {
              // @ts-ignore
              let choosenRoute = routes.filter(o => o.id === parseInt(assessmentRoute))
              chosenAssessmentRoute = choosenRoute[0].id
              chosenAssessmentName = choosenRoute[0].name

              if(preSelectedAssessmentRoute) {
                  // @ts-ignore
                  choosenRoute = routes.filter(o => o.id === parseInt(assessmentRoute))
                  chosenAssessmentRoute = choosenRoute[0].id
                  chosenAssessmentName = choosenRoute[0].name
              }
          }
      }
      if(assessmentRoute === null && routes.length) {
          chosenAssessmentRoute = routes[0].id
      }


      let filledInLetter = letter.replace('[ondernemer]', name.split(" ")[0])
      filledInLetter = filledInLetter.replaceAll('[venture]', venture)
      filledInLetter = filledInLetter.replace('[email begeleider]', userContext.emailAddress)
      filledInLetter = filledInLetter.replace('[begeleider]', userContext.name)
      filledInLetter = filledInLetter.replaceAll('[assessment_name]', chosenAssessmentName)


      setMessage(filledInLetter)
      setFormValid(!formValid)
      setFormData({
          name,
          venture,
          emailAddress,
          language,
          assessmentRoute: chosenAssessmentRoute,
          tags
      })
  }

  const closeModal = () => {
      formikRef.current?.resetForm()
      setFormData(initialFormValues)
      setTags([])
      setFormValid(false)
      onToggle()
  }

    useEffect(() => {
        if(isOpen) {
            document.getElementById('assessment-invite-name')?.focus()
            setFormData(initialFormValues)
            setTags([])
            setNameValidation(true)
        } else {
            setNameValidation(false)
        }
    }, [isOpen]);


    return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <p className={styles.name}>Invite</p>
        <button className={styles.close} onClick={closeModal}>
          X
        </button>
      </div>
      <h2 className={styles.title}>{`Request scan for`}</h2>
        {!formValid && (
        <section>
        <Formik
            enableReinitialize
            validationSchema={validationSchema}
            initialValues={formData}
            onSubmit={submitForm}
            // @ts-ignore
            innerRef={formikRef}
        >
            {({ values, errors, touched, handleChange, handleSubmit, handleBlur, isValid }) => (
                <form className={styles.formContainer} onSubmit={handleSubmit}>
                    <div className={styles.inputContainer}>
                        {errors.name && touched.name ? (
                            <span className={styles.error}>Name is required</span>
                        ) : null}
                        <Field
                            id="assessment-invite-name"
                            name='name'
                            as={Input}
                            type='text'
                            placeholder='Name'
                            onChange={handleChange}
                            value={values.name}
                            autoFocus
                            onBlur={(e: React.FocusEvent<any>) => {
                                if(nameValidation) {
                                    handleBlur(e)
                                }
                            }}
                        />
                    </div>
                    <div className={styles.inputContainer}>
                        {errors.emailAddress && touched.emailAddress ? (
                            errors.emailAddress.includes('valid')
                                ? <span className={styles.error}>Email format is incorrect</span>
                                : <span className={styles.error}>Email address is required</span>
                        ) : null}
                        <Field
                            name='emailAddress'
                            as={Input}
                            type='text'
                            placeholder='Email address'
                            onChange={handleChange}
                            value={values.emailAddress}
                        />
                    </div>
                    <div className={styles.inputContainer}>
                        {errors.venture && touched.venture ? (
                            <span className={styles.error}>Company name is required</span>
                        ) : null}
                        <Field
                            name='venture'
                            as={Input}
                            type='text'
                            placeholder='Company name'
                            onChange={handleChange}
                            value={values.venture}
                        />
                    </div>
                    <div className={styles.inputContainer}>
                        <TagFormInput suggestions={tagSuggestions} tags={tags} setTags={(tags: [Tag]) => {
                            setTags(tags)
                        }}/>
                    </div>
                    <div className={styles.selectContainer}>
                        <Field
                            className={styles.select}
                            name='language'
                            as="select"
                            placeholder='Language'
                            onChange={handleChange}
                            value={values.language}
                        >
                            <option value="nl">Dutch</option>
                            <option value="en">English</option>
                        </Field>
                    </div>
                    {routes && routes.length > 1 && (
                    <div className={styles.selectContainer}>
                        <Field
                            className={styles.select}
                            name='assessmentRoute'
                            as="select"
                            placeholder='Assessment'
                            onChange={handleChange}
                            disabled={preSelectedAssessmentRoute !== null}
                            value={preSelectedAssessmentRoute}
                        >
                            {
                                routes.map((el) => (

                                    <option selected={preSelectedAssessmentRoute === el.id} value={el.id}>{el.name}</option>
                                ))
                            }
                        </Field>
                    </div>
                    )}
                    <div className={styles.modalButtons}>
                        <ButtonPageLayout type='submit' disabled={!isValid}>Next</ButtonPageLayout>
                    </div>
                </form>
            )}
        </Formik>
      </section>
    )}
    {formValid && message !== null && (
        <section>
          <textarea
            className={styles.textField}
            value={typeof message === 'string' ? message : '' }
            onChange={(e) => setMessage(e.target.value)}
          ></textarea>
                <section className={styles.modalButtons}>
                    <button
                        className={styles.send}
                        onClick={() => {
                            onClick(formData, message)
                            setTimeout(() => {
                                formikRef.current?.resetForm()
                                setFormData(initialFormValues)
                                setTags([])
                                setFormValid(false)
                            }, 2000)
                        }}
                    >{`Send`}</button>
                    <button
                    className={styles.cancel}
                    onClick={closeModal}
                    >{`Cancel`}</button>
                </section>
            {sendingSuccessful && (
                <span className={styles.sendSuccessful}>Mail sent succesfully</span>
            )}
            {errorFound && (
                <span className={styles.sendFailed}>Oops, something went wrong with sending the invite, don't worry, we're notified of the issue</span>
            )}
        </section>
    )}
    </div>
  );
};
